'use client';

import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmark, faUser, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import { UserMenu } from "@/components/layout/UserMenu";
import { useUser } from "@/context/UserContext";
import { useLayout } from "@/context/LayoutContext";
import { useCategoryFilter } from "@/context/CategoryFilterContext";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/colecoes", label: "Coleções" },
  { href: "/videos-institucionais", label: "Vídeos institucionais" },
  { href: "/removedor-de-fundo", label: "Removedor de Fundo" }
] as const;

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { user, isLoading } = useUser();
  const { updateBounds } = useLayout();
  const { resetAll } = useCategoryFilter();

  // Refs for tracking element positions
  const logoRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Apenas verifica se tem user carregado
  const isAuthenticated = Boolean(user);

  // Handler for logo click - reset filters, pagination and navigate to home
  const handleLogoClick = useCallback(() => {
    resetAll();
    router.push('/');
  }, [resetAll, router]);

  // Update layout bounds when elements mount or resize
  const updateLayoutBounds = useCallback(() => {
    if (logoRef.current) {
      const logoRect = logoRef.current.getBoundingClientRect();
      updateBounds({ logoLeft: logoRect.left });
    }
    if (menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      updateBounds({ menuRight: menuRect.right });
    }
    if (headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      updateBounds({ headerHeight: headerRect.height });
    }
  }, [updateBounds]);

  useEffect(() => {
    updateLayoutBounds();
    window.addEventListener('resize', updateLayoutBounds);
    return () => window.removeEventListener('resize', updateLayoutBounds);
  }, [updateLayoutBounds]);

  const closeMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setMenuOpen(false);
      setIsClosing(false);
    }, 500);
  };

  // Close menu and navigate - ensures navigation happens even on mobile
  const closeMenuAndNavigate = (href: string) => {
    setIsClosing(true);
    // For /login, use window.location.href to avoid client-side navigation race conditions
    // For other routes, router.push is fine
    if (href === '/login') {
      window.location.href = href;
    } else {
      router.push(href as any);
    }
    setTimeout(() => {
      setMenuOpen(false);
      setIsClosing(false);
    }, 500);
  };

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
    } else {
      setMenuOpen(true);
    }
  };
  const isActive = (href: string) => (href === "/" ? pathname === href : pathname?.startsWith(href));

  return (
    <header ref={headerRef} className="fixed top-0 left-0 z-50 w-full bg-brand-dark/80 backdrop-blur-xl shadow-soft border-b border-white/10">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <button 
            ref={logoRef} 
            onClick={handleLogoClick}
            className="flex items-center gap-2 shrink-0"
            type="button"
          >
            <Image
              src="/images/logo/logo.png"
              alt="Logo"
              width={200}
              height={30}
              className="h-4 sm:h-6 w-auto object-contain"
              priority
            />
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={clsx(
                "text-sm font-medium transition-colors hover:text-brand-secondary",
                isActive(item.href) ? "text-brand-secondary" : "text-slate-300"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div ref={menuRef} className="flex items-center gap-3">
          {/* Mostrar "Seja Premium" apenas para usuários não premium */}
          {(!user?.isPremium) && (
            <a
              href="/planos"
              className="relative hidden items-center gap-2 rounded-full bg-brand-secondary px-5 py-2 text-sm font-bold text-brand-dark shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-transform hover:scale-105 hover:shadow-[0_0_25px_rgba(34,197,94,0.6)] active:scale-95 lg:inline-flex"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M21.8382 11.1263L21.609 13.5616C21.2313 17.5742 21.0425 19.5805 19.8599 20.7902C18.6773 22 16.9048 22 13.3599 22H10.6401C7.09517 22 5.32271 22 4.14009 20.7902C2.95748 19.5805 2.76865 17.5742 2.391 13.5616L2.16181 11.1263C1.9818 9.2137 1.8918 8.25739 2.21899 7.86207C2.39598 7.64823 2.63666 7.5172 2.89399 7.4946C3.36968 7.45282 3.96708 8.1329 5.16187 9.49307C5.77977 10.1965 6.08872 10.5482 6.43337 10.6027C6.62434 10.6328 6.81892 10.6018 6.99526 10.5131C7.31351 10.3529 7.5257 9.91812 7.95007 9.04852L10.1869 4.46486C10.9888 2.82162 11.3898 2 12 2C12.6102 2 13.0112 2.82162 13.8131 4.46485L16.0499 9.04851C16.4743 9.91812 16.6865 10.3529 17.0047 10.5131C17.1811 10.6018 17.3757 10.6328 17.5666 10.6027C17.9113 10.5482 18.2202 10.1965 18.8381 9.49307C20.0329 8.1329 20.6303 7.45282 21.106 7.4946C21.3633 7.5172 21.604 7.64823 21.781 7.86207C22.1082 8.25739 22.0182 9.2137 21.8382 11.1263ZM12.9524 12.699L12.8541 12.5227C12.4741 11.841 12.2841 11.5002 12 11.5002C11.7159 11.5002 11.5259 11.841 11.1459 12.5227L11.0476 12.699C10.9397 12.8927 10.8857 12.9896 10.8015 13.0535C10.7173 13.1174 10.6125 13.1411 10.4028 13.1886L10.2119 13.2318C9.47396 13.3987 9.10501 13.4822 9.01723 13.7645C8.92945 14.0468 9.18097 14.3409 9.68403 14.9291L9.81418 15.0813C9.95713 15.2485 10.0286 15.3321 10.0608 15.4355C10.0929 15.5389 10.0821 15.6504 10.0605 15.8734L10.0408 16.0765C9.96476 16.8613 9.92674 17.2538 10.1565 17.4282C10.3864 17.6027 10.7318 17.4436 11.4227 17.1255L11.6014 17.0432C11.7978 16.9528 11.8959 16.9076 12 16.9076C12.1041 16.9076 12.2022 16.9528 12.3986 17.0432L12.5773 17.1255C13.2682 17.4436 13.6136 17.6027 13.8435 17.4282C14.0733 17.2538 14.0352 16.8613 13.9592 16.0765L13.9395 15.8734C13.9179 15.6504 13.9071 15.5389 13.9392 15.4355C13.9714 15.3321 14.0429 15.2485 14.1858 15.0813L14.316 14.9291C14.819 14.3409 15.0706 14.0468 14.9828 13.7645C14.895 13.4822 14.526 13.3987 13.7881 13.2318L13.5972 13.1886C13.3875 13.1411 13.2827 13.1174 13.1985 13.0535C13.1143 12.9896 13.0603 12.8927 12.9524 12.699Z" fill="#e5a50a"/>
              </svg>
              Seja Premium
            </a>
          )}

          {/* Botão Tutorial - visível apenas para usuários logados */}
          {user && (
            <button
              type="button"
              data-tutorial-target="tutorial-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('tutorial:open'))}
              className="hidden h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 transition-all hover:bg-white/10 hover:text-amber-400 hover:border-amber-400/30 lg:inline-flex"
              aria-label="Abrir tutorial"
              title="Tutorial"
            >
              <FontAwesomeIcon icon={faCircleQuestion} className="h-4 w-4" />
            </button>
          )}

          {/* Durante o carregamento, mostra placeholder para evitar flash do botão "Entrar" */}
          {isLoading ? (
            <div className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/5 lg:inline-flex">
              <div className="h-4 w-4 animate-pulse rounded-full bg-white/30" />
            </div>
          ) : user ? (
            <UserMenu />
          ) : (
            <a
              href="/login"
              onClick={(e) => { e.preventDefault(); window.location.href = '/login'; }}
              className="hidden items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-white/10 hover:border-white/40 active:scale-95 lg:inline-flex"
            >
              <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
              Entrar
            </a>
          )}

          <button
            type="button"
            onClick={toggleMenu}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white shadow-soft transition-transform hover:scale-105 active:scale-95 lg:hidden"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className={clsx(
        "fixed inset-0 z-[9000] h-screen w-screen flex-col bg-brand-dark lg:hidden animate__animated animate__faster",
        (menuOpen || isClosing) ? "flex" : "hidden",
        isClosing ? "animate__slideOutRight" : (menuOpen ? "animate__slideInRight" : "")
      )}>
        <div className="flex items-center justify-between border-b border-white/10 bg-brand-dark px-4 py-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Image
              src="/images/logo/logo.png"
              alt="Logo"
              width={273}
              height={40}
              className="h-10 w-auto"
              priority
            />
          </div>
          <button
            type="button"
            onClick={closeMenu}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white shadow-soft transition-transform hover:scale-105 active:scale-95"
            aria-label="Fechar menu"
          >
            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-brand-dark px-4 py-6 sm:px-6">
          <nav className="flex flex-col gap-4" aria-label="Menu móvel">
            {navLinks.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => closeMenuAndNavigate(link.href)}
                className={clsx(
                  "flex w-full items-center justify-between rounded-lg px-3 py-2 text-base font-semibold transition-all hover:scale-[1.02] text-left",
                  isActive(link.href) ? "bg-brand-secondary/10 text-brand-secondary" : "text-slate-300 hover:bg-white/5 hover:text-white"
                )}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="ml-3 h-2 w-2 rounded-full bg-brand-secondary" aria-hidden />
                )}
              </button>
            ))}
          </nav>
          <div className="mt-6 flex flex-col gap-3">
            {/* Mostrar "Seja Premium" apenas para usuários não premium (menu mobile) */}
            {(!user?.isPremium) && (
              <a
                href="/planos"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-secondary px-5 py-3 text-sm font-bold text-brand-dark shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-transform hover:scale-[1.02] active:scale-95"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M21.8382 11.1263L21.609 13.5616C21.2313 17.5742 21.0425 19.5805 19.8599 20.7902C18.6773 22 16.9048 22 13.3599 22H10.6401C7.09517 22 5.32271 22 4.14009 20.7902C2.95748 19.5805 2.76865 17.5742 2.391 13.5616L2.16181 11.1263C1.9818 9.2137 1.8918 8.25739 2.21899 7.86207C2.39598 7.64823 2.63666 7.5172 2.89399 7.4946C3.36968 7.45282 3.96708 8.1329 5.16187 9.49307C5.77977 10.1965 6.08872 10.5482 6.43337 10.6027C6.62434 10.6328 6.81892 10.6018 6.99526 10.5131C7.31351 10.3529 7.5257 9.91812 7.95007 9.04852L10.1869 4.46486C10.9888 2.82162 11.3898 2 12 2C12.6102 2 13.0112 2.82162 13.8131 4.46485L16.0499 9.04851C16.4743 9.91812 16.6865 10.3529 17.0047 10.5131C17.1811 10.6018 17.3757 10.6328 17.5666 10.6027C17.9113 10.5482 18.2202 10.1965 18.8381 9.49307C20.0329 8.1329 20.6303 7.45282 21.106 7.4946C21.3633 7.5172 21.604 7.64823 21.781 7.86207C22.1082 8.25739 22.0182 9.2137 21.8382 11.1263ZM12.9524 12.699L12.8541 12.5227C12.4741 11.841 12.2841 11.5002 12 11.5002C11.7159 11.5002 11.5259 11.841 11.1459 12.5227L11.0476 12.699C10.9397 12.8927 10.8857 12.9896 10.8015 13.0535C10.7173 13.1174 10.6125 13.1411 10.4028 13.1886L10.2119 13.2318C9.47396 13.3987 9.10501 13.4822 9.01723 13.7645C8.92945 14.0468 9.18097 14.3409 9.68403 14.9291L9.81418 15.0813C9.95713 15.2485 10.0286 15.3321 10.0608 15.4355C10.0929 15.5389 10.0821 15.6504 10.0605 15.8734L10.0408 16.0765C9.96476 16.8613 9.92674 17.2538 10.1565 17.4282C10.3864 17.6027 10.7318 17.4436 11.4227 17.1255L11.6014 17.0432C11.7978 16.9528 11.8959 16.9076 12 16.9076C12.1041 16.9076 12.2022 16.9528 12.3986 17.0432L12.5773 17.1255C13.2682 17.4436 13.6136 17.6027 13.8435 17.4282C14.0733 17.2538 14.0352 16.8613 13.9592 16.0765L13.9395 15.8734C13.9179 15.6504 13.9071 15.5389 13.9392 15.4355C13.9714 15.3321 14.0429 15.2485 14.1858 15.0813L14.316 14.9291C14.819 14.3409 15.0706 14.0468 14.9828 13.7645C14.895 13.4822 14.526 13.3987 13.7881 13.2318L13.5972 13.1886C13.3875 13.1411 13.2827 13.1174 13.1985 13.0535C13.1143 12.9896 13.0603 12.8927 12.9524 12.699Z" fill="#e5a50a"/>
                </svg>
                Seja Premium
              </a>
            )}
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  window.dispatchEvent(new CustomEvent('tutorial:open'));
                }}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-amber-400/20 bg-amber-400/5 px-5 py-3 text-sm font-semibold text-amber-400 transition-all hover:bg-amber-400/10 hover:border-amber-400/40 active:scale-95"
              >
                <FontAwesomeIcon icon={faCircleQuestion} className="h-4 w-4" />
                Tutorial
              </button>
            )}
            {!isAuthenticated && (
              <button
                type="button"
                onClick={() => closeMenuAndNavigate('/login')}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-white/10 hover:border-white/40 active:scale-95"
              >
                <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                Entrar
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
